import type { MeasureInfo } from './model';

export interface PlaybackStep {
  measureIndex: number;
  /** 1 on the first pass through a repeated section, 2 on the second, and so on. */
  pass: number;
}

export interface UnrollResult {
  order: PlaybackStep[];
  /** True if unrolling stopped at the safety limit (a malformed repeat structure). */
  truncated: boolean;
}

const MAX_STEPS = 10_000;

/**
 * Unroll repeats, voltas and jumps into a linear playback order.
 *
 * - `|: ... :|` repeats `times` times (default 2). A backward repeat with no forward repeat
 *   goes back to the start of the piece or to the end of the previous repeated section.
 * - Ending N is played on pass N. A backward repeat inside an ending goes back while there is
 *   a higher ending number in the same group.
 * - D.C. and D.S. are taken once. After the jump, repeats are not taken, only the last ending
 *   of each group is played, "Fine" ends the piece and "To Coda" jumps to the coda.
 */
export function unrollMeasures(measures: MeasureInfo[]): UnrollResult {
  const order: PlaybackStep[] = [];
  const n = measures.length;
  const segnoIndex = measures.findIndex((m) => m.segno);
  const codaIndex = measures.findIndex((m) => m.coda);
  const repeatCounts = new Map<number, number>();
  let sectionStart = 0;
  let pass = 1;
  let jumped = false;
  let i = 0;

  while (i < n) {
    if (order.length >= MAX_STEPS) return { order, truncated: true };
    const m = measures[i] as MeasureInfo;

    if (m.forwardRepeat && !m.ending && sectionStart !== i) {
      sectionStart = i;
      pass = 1;
    }

    if (m.ending) {
      const target = jumped ? m.ending.groupMax : pass;
      if (!m.ending.numbers.includes(target)) {
        i++;
        continue;
      }
    }

    order.push({ measureIndex: i, pass });

    if (jumped && m.fine) break;
    if (jumped && m.toCoda && codaIndex > i) {
      i = codaIndex;
      continue;
    }

    if (m.backwardRepeat && !jumped) {
      if (m.ending) {
        if (pass < m.ending.groupMax) {
          pass++;
          i = sectionStart;
          continue;
        }
      } else {
        const count = repeatCounts.get(i) ?? 0;
        if (count < m.backwardRepeat.times - 1) {
          repeatCounts.set(i, count + 1);
          pass++;
          i = sectionStart;
          continue;
        }
        repeatCounts.delete(i);
      }
      sectionStart = i + 1;
      pass = 1;
      i++;
      continue;
    }

    if (!jumped && (m.daCapo || m.dalSegno)) {
      jumped = true;
      pass = 1;
      i = m.daCapo ? 0 : Math.max(segnoIndex, 0);
      sectionStart = i;
      continue;
    }

    if (m.ending && !measures[i + 1]?.ending) {
      sectionStart = i + 1;
      pass = 1;
    }
    i++;
  }
  return { order, truncated: false };
}
